import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, PlusCircle, ClipboardList, Wallet, CalendarRange,
  Palette, Ticket, BarChart3, Leaf, Settings as SettingsIcon, LogOut, Menu, X, Globe, Shield, Scale, UserCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";

const topNav = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard" },
  { to: "/calendar", label: "Calendrier", icon: CalendarDays, id: "calendar" },
  { to: "/create-event", label: "Créer un événement", icon: PlusCircle, id: "create-event" },
];

const eventNav = [
  { to: "/events", label: "Événement", icon: LayoutDashboard, id: "events" },
  { to: "/tasks", label: "Tâches", icon: ClipboardList, id: "tasks" },
  { to: "/budget", label: "Budget", icon: Wallet, id: "budget" },
  { to: "/devis", label: "Comparateur devis IA", icon: Scale, id: "devis" },
  { to: "/agenda", label: "Programme", icon: CalendarRange, id: "agenda" },
  { to: "/branding", label: "Branding", icon: Palette, id: "branding" },
  { to: "/landing-editor", label: "Landing page", icon: Globe, id: "landing-editor" },
  { to: "/invites", label: "Billetterie", icon: Ticket, id: "invites" },
  { to: "/checkins", label: "Suivi présence", icon: UserCheck, id: "checkins" },
  { to: "/statistics", label: "Statistiques", icon: BarChart3, id: "statistics" },
  { to: "/carbon", label: "Bilan carbone", icon: Leaf, id: "carbon" },
];

function NavItem({ to, label, icon: Icon, id, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${id}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
          isActive
            ? "bg-indigo-500/15 text-indigo-300 font-semibold"
            : "text-slate-400 hover:bg-slate-800/70 hover:text-white"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { events, company, loaded } = useData();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const codeExpired = !!(user?.code_expires_at && !user?.is_admin && new Date(user.code_expires_at) < new Date());

  React.useEffect(() => {
    if (loaded && !user?.is_admin && company && Object.keys(company).length >= 0 && !company.companyName) {
      navigate("/profile");
    }
  }, [loaded, company, user, navigate]);

  const doLogout = () => { logout(); navigate("/"); };
  const close = () => setMobileOpen(false);

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-slate-900 text-slate-300 flex flex-col h-full">
      <div className="px-6 py-5 flex items-center gap-2 border-b border-slate-800">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-emerald-400 flex items-center justify-center">
          <Leaf className="h-5 w-5 text-white" />
        </div>
        <span className="font-display font-extrabold text-white text-lg tracking-tight">JustPlanIt</span>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {topNav.map((n) => <NavItem key={n.id} {...n} onClick={close} />)}
        {events.length > 0 && (
          <div className="pt-4">
            <p className="px-4 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Gestion de l'événement
            </p>
            {eventNav.map((n) => <NavItem key={n.id} {...n} onClick={close} />)}
          </div>
        )}
      </nav>
      <div className="px-3 py-3 border-t border-slate-800 space-y-1">
        {user?.is_admin && <NavItem to="/admin" label="Espace admin" icon={Shield} id="admin" onClick={close} />}
        <NavItem to="/settings" label="Paramètres" icon={SettingsIcon} id="settings" onClick={close} />
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-slate-800/70 hover:text-white transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" />
          <span>Déconnexion</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="hidden lg:block">{Sidebar}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={close} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="text-sm text-muted-foreground hidden sm:block">Espace organisateur</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold leading-tight">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || "U")}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 jpi-fade-up">
            {codeExpired && (
              <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="code-expired-banner">
                <Shield className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Votre code d'accès a expiré. Vous pouvez consulter vos événements, mais la création et la modification sont bloquées. Contactez l'administrateur pour le renouveler.</span>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
